import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Ayuda } from './Ayuda';
import './ui.css';

/**
 * Una ventana sobre la pantalla: «Eliminar sucursal», «Asignar personal». Sale de
 * esos frames de Sucursales en el Figma, y la usan igual las demás maestras para
 * confirmar una baja.
 *
 *   Título
 *   Descripción
 *   Contenido (opcional)
 *   ─────────────────────────────
 *   [lo de la izquierda]        [Cancelar] [Acción]
 *
 * ## Por qué `<dialog>` y no un `div` encima de todo
 *
 * Con `showModal()` el navegador hace lo que a mano cuesta mantener: el foco no
 * se escapa a la página de atrás, lo de atrás queda inerte para el lector de
 * pantalla, y la ventana va por encima de todo sin pelear con ningún `z-index`.
 * Un `div` tendría que reimplementar las tres cosas.
 *
 * **`info` y `descripcion` no son lo mismo** (#121). `info` es la explicación
 * —«Se guarda en el catálogo del taller.»—, y va tras el ⓘ del título.
 * `descripcion` es lo que se lee siempre: la pregunta de una confirmación
 * —«¿Eliminar la sucursal Centro?»— o un dato —«Se leyeron 51 filas…»—.
 *
 * Es controlada: `abierta` decide, y **solo se cierra con sus botones** (#124).
 * Ni tocar el fondo ni Escape la cierran: con un formulario a medias, un toque
 * sin querer en el celular perdía lo escrito.
 */
export function Ventana({
  abierta,
  titulo,
  info,
  descripcion,
  children,
  acciones,
  izquierda,
  ancho = 480,
}: {
  abierta: boolean;
  titulo: string;
  /** La explicación de la ventana, en el globo del ⓘ junto al título. */
  info?: string;
  /** Lo que se lee siempre: la pregunta de una confirmación, o un dato. */
  descripcion?: ReactNode;
  children?: ReactNode;
  /** Los botones de la derecha del pie: «Cancelar» y la acción. */
  acciones: ReactNode;
  /** Lo de la izquierda del pie: «4 seleccionadas». */
  izquierda?: ReactNode;
  /** Ancho en px. La de confirmar usa 480; la de una lista, más. */
  ancho?: number;
}) {
  const dialogo = useRef<HTMLDialogElement>(null);
  const idTitulo = useId();
  const idDescripcion = useId();
  const idInfo = useId();

  useEffect(() => {
    const d = dialogo.current;
    if (!d) return;
    if (abierta && !d.open) abrir(d);
    if (!abierta && d.open) d.close();
  }, [abierta]);

  return (
    <dialog
      ref={dialogo}
      className="ui-ventana"
      style={{ width: ancho }}
      aria-labelledby={idTitulo}
      // La explicación también se lee al abrir, aunque esté tras el ⓘ (#121).
      aria-describedby={[descripcion && idDescripcion, info && idInfo].filter(Boolean).join(' ') || undefined}
      // Escape dispara `cancel`, y el navegador cerraría el diálogo solo.
      onCancel={(e) => e.preventDefault()}
      // Ese freno no siempre alcanza: Chromium deja cancelar un solo Escape por
      // gesto, y al segundo seguido cierra el diálogo igual. Si React dice que
      // sigue abierta, se vuelve a abrir en vez de quedar invisible.
      onClose={() => {
        if (abierta && dialogo.current) abrir(dialogo.current);
      }}
    >
      {abierta && (
        <div className="ui-ventana__cuerpo">
          <div className="ui-ventana__cabecera">
            <h2 id={idTitulo} className="ui-ventana__titulo">
              {titulo}
            </h2>
            {info && <Ayuda tema={titulo} texto={info} tono="titulo" />}
          </div>
          {info && (
            <span id={idInfo} hidden>
              {info}
            </span>
          )}
          {descripcion && (
            <p id={idDescripcion} className="ui-ventana__descripcion">
              {descripcion}
            </p>
          )}
          {children}
          <div className="ui-ventana__pie">
            <div className="ui-ventana__izquierda">{izquierda}</div>
            <div className="ui-ventana__acciones">{acciones}</div>
          </div>
        </div>
      )}
    </dialog>
  );
}

/** Lo primero que se puede tocar del contenido: un campo, o si no hay, un botón del pie. */
const ENFOCABLE =
  'input:not([type="hidden"]):not([hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]):not(.ui-ayuda__boton)';

/**
 * Abre el diálogo y lleva el foco al contenido. Sin esto, `showModal()` enfoca
 * el primer elemento enfocable, que es el ⓘ del título (#121): lo que se escribe
 * no caería en el primer campo.
 */
function abrir(d: HTMLDialogElement) {
  d.showModal();
  d.querySelector<HTMLElement>(ENFOCABLE)?.focus();
}
